import { FC, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { RUMHelpers } from 'services/observability/datadog';
import { Product, Team } from 'teams';
import { usePaginationContext } from 'utils/contexts/pagination';
import { localizeFileSize } from 'utils/i18nHelpers';
import { ByteUnit } from 'utils/l10n';
import noop, { NoOp } from 'utils/noop';

import { usePanelContext } from './Panel';
import { useTakeoverContext } from './Takeover';
import { UploadAssetsContext, UploadAssetsContextState } from './UploadAssetsContext';
import {
  DropZoneArea,
  ImageMods,
  NewImage,
  PromiseStatus,
  TakeoverIntent,
  UploadAssetError,
  UploadSuccess,
  inputAccept,
} from '../types';
import { useTranslations } from '../hooks/useTranslations';
import { useAssetsQueryContext } from './AssetsQueryContext';
import {
  arrayPartition,
  getEditChanges,
  getInvalidImgObj,
  isValidPendingAsset,
  processImage,
  updateAssetData,
  uploadFile,
} from '../utils/images';
import { defaults, ROOT_FOLDER_NAME } from '../constants';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';

export type Props = {
  isPanel?: boolean;
  canEditBeforeUpload?: boolean;
};

export const UploadAssetsContextProvider: FC<React.PropsWithChildren<Props>> = ({
  isPanel = false,
  canEditBeforeUpload = false,
  children,
}) => {
  const dispatchHook = useDispatch();
  const { refetch: refetchAssets, assets, folderId } = useAssetsQueryContext();
  const isCreativeLibraryAssetsFolderPanelFFEnabled = useFeatureFlag(
    'CreativeLibraryAssetsFolderPanel',
    false,
  );
  const { t } = useTranslations();
  const newAssetBatch = useRef<number>(0);
  const [editingImage, setEditingImage] = useState<number | null>(null);
  const [activeDropZone, setActiveDropZone] = useState<DropZoneArea>(DropZoneArea.None);
  const [maxBatchSize, setMaxBatchSize] = useState<number>(defaults.maxBatchSize);
  const [maxFileSize, setMaxFileSize] = useState<number>(defaults.maxFileSize);
  const [maxImgDimension, setMaxImgDimension] = useState<number>(defaults.maxImgDimension);
  const [pendingImages, setPendingImages] = useState<NewImage[]>([]);
  const { currentPage, setCurrentPage } = usePaginationContext();
  const [isUploading, setIsUploading] = useState(false);

  const {
    onEditFiles: onEditFilesIndex,
    onNewFiles: onNewFilesIndex,
    onUploadComplete: onUploadCompleteIndex,
    onClose: onCloseTakeover,
    currentIntent,
  } = useTakeoverContext();
  const { onNewFiles: onNewFilesPanel, onUploadComplete: onUploadCompletePanel } =
    usePanelContext();

  const onNewFiles = isPanel ? onNewFilesPanel : onNewFilesIndex;
  const onUploadComplete = isPanel ? onUploadCompletePanel : onUploadCompleteIndex;

  const pendingUploadCount = pendingImages.filter(isValidPendingAsset).length;

  // should only be called if canEditBeforeUpload
  const clearPendingUploads = () => {
    setPendingImages([]);
  };

  const removeCompletedUploads = () => {
    setPendingImages((pendingImgs) =>
      pendingImgs.filter((pImg) => {
        return (
          pImg.processStatus !== PromiseStatus.Rejected &&
          ![PromiseStatus.Aborted, PromiseStatus.Fulfilled, PromiseStatus.Rejected].includes(
            pImg.uploadStatus,
          )
        );
      }),
    );
  };

  const removeSelectedUploads = (ids: string[]) => {
    setPendingImages((pendingImgs) => {
      const filteredImgs = pendingImgs.filter((pImg) => !ids.includes(pImg.id));
      if (filteredImgs.length === 0 && currentIntent === TakeoverIntent.EDIT) {
        onCloseTakeover(false, noop);
      }
      return filteredImgs;
    });
  };

  // the list of ids must be complete (otherwise isSelected is set back to false)
  const setSelectedAssets = (ids: string[]) => {
    setPendingImages((pendingImgs) => {
      return pendingImgs.map((pi) => ({
        ...pi,
        isSelected: ids.includes(pi.id),
      }));
    });
  };

  const handleSetAbortUpload = (id: string, abortController: AbortController) => {
    setPendingImages((pendingImgs) => {
      const selectedImgId = pendingImgs.findIndex((pImg) => pImg.id === id);
      const newPendingImgs = [...pendingImgs];
      newPendingImgs[selectedImgId].abortUpload = () => abortController.abort();
      return newPendingImgs;
    });
  };

  const handleModifyPendingAssets = (ids: string[], values: Record<string, any>) => {
    const newPendingImages = [...pendingImages];
    ids.forEach((id) => {
      const selectedImageId = pendingImages.findIndex((pImg) => pImg.id === id);
      // only execute if image is found
      if (selectedImageId >= 0) {
        newPendingImages[selectedImageId].file = {
          ...newPendingImages[selectedImageId].file,
          ...values,
        };
        setPendingImages(newPendingImages);
      }
    });
  };

  const handleUploadProgress = (
    id: string,
    pct: number,
    status: PromiseStatus = PromiseStatus.Pending,
  ) => {
    setPendingImages((pendingImgs) => {
      const selectedImgId = pendingImgs.findIndex((pImg) => pImg.id === id);
      const newPendingImgs = [...pendingImgs];
      if (selectedImgId >= 0) {
        newPendingImgs[selectedImgId].uploadPct = pct;
        newPendingImgs[selectedImgId].uploadStatus = status;
        if (status !== PromiseStatus.Pending) {
          newPendingImgs[selectedImgId].abortUpload = undefined;
        }
        if (status === PromiseStatus.Rejected) {
          newPendingImgs[selectedImgId].uploadError = UploadAssetError.Failed;
        } else if (status === PromiseStatus.Aborted) {
          newPendingImgs[selectedImgId].uploadError = UploadAssetError.Aborted;
        }
      }
      return newPendingImgs;
    });
  };

  // STEP 3: attempt to upload each file in the array of new files
  const handleUpload = (newFiles: NewImage[]) => {
    setIsUploading(true);
    // separate files based on the outcome of processImage from STEP 2 (valid and invalid images)
    const [filesToUpload, filesToDismiss] = arrayPartition(
      newFiles,
      (file) => file.processStatus === PromiseStatus.Fulfilled,
    );

    const uploadSuccesses: UploadSuccess[] = [];
    const uploadFailures: Record<string, any>[] = [];
    const promises = filesToUpload.map((file) =>
      uploadFile(file, handleUploadProgress, handleSetAbortUpload),
    );
    const wasUploadingToCurrentFolder = newFiles.some(
      (f) => f.selected.destinationFolderId === folderId,
    );

    // attempt to upload only files that have passed all tests from STEPS 1 & 2
    Promise.allSettled(promises)
      .then((results) => {
        results.forEach((result) => {
          if (result.status === PromiseStatus.Fulfilled) {
            const successResult = result.value as UploadSuccess;
            uploadSuccesses.push(successResult);

            dispatchHook(
              addPositiveToast(
                <p>
                  {t('success.assetAdded')}
                  <strong>{successResult.asset.assetName}</strong>
                </p>,
              ),
            );
          } else {
            uploadFailures.push(result.reason); // "reason" = { id: NewImage.id, err }
          }
        });
      })
      .finally(() => {
        setIsUploading(false);
        onCloseTakeover(false, noop);

        if (
          uploadSuccesses.length &&
          (wasUploadingToCurrentFolder || !isCreativeLibraryAssetsFolderPanelFFEnabled)
        ) {
          if (currentPage === 1) {
            refetchAssets();
          } else {
            setCurrentPage(1);
          }
        }

        // respond to completed upload for index/panel
        onUploadComplete({
          filesToDismiss,
          newFiles,
          removeSelectedUploads,
          setPendingImages,
          uploadFailures,
          uploadSuccesses,
        });

        setPendingImages([]);
        /* Report upload failures to DataDog:
         * Upload errors occur after a selected file (which has been pre-validated for file size
         * and type) fails the upload process. It may be an indication of a more serious issue
         * with server load / concurrency, or secondary processing of the file */
        if (uploadFailures.length) {
          const errorName = `FILE_UPLOAD_FAILURE`;
          const errorMessage = `Creative Library Upload Failure`;
          const componentError = new Error(errorMessage);
          componentError.name = errorName;
          const uploadFailureDetail = uploadFailures.map(({ id, err }) => {
            const failedFile = newFiles.find((file) => file.id === id);
            return {
              fileDetails: failedFile?.selected,
              error: err,
              failureReason:
                err.code === 'ERR_CANCELED' ? PromiseStatus.Aborted : PromiseStatus.Rejected,
            };
          });

          RUMHelpers.addRUMError(
            componentError,
            {
              name: errorName,
              productName: Product.CREATIVE_LIBRARY,
              teamName: Team.CONTENT_TEAM,
            },
            {
              uploadFailureDetail,
            },
          );
        }
      });
  };

  const handleUploadPending = () => {
    const validPendingUploads = pendingImages.filter(isValidPendingAsset);
    handleUpload(validPendingUploads);
  };

  // STEP 1: process new file(s) following drop/select
  const handleNewFiles = (files: File[], cb?: NoOp) => {
    const re = new RegExp(
      `^(image/)${inputAccept.map((mimeType) => `(${mimeType.split('/')[1]})?`).join('')}$`,
    );
    onNewFiles(files.length);

    // separate unsupported file types
    const [filesCorrectType, filesIncorrectType] = arrayPartition(files, (newFile) =>
      re.test(newFile.type),
    );
    // separate files that exceed our individual file size maximum
    const [goodFiles, filesIncorrectSize] = arrayPartition(
      filesCorrectType,
      (newFile) => newFile.size <= maxFileSize,
    );
    // separate files that exceed the max batch size (max simultaneous upload limit)
    const filesOverBulkLimit = goodFiles.splice(maxBatchSize);

    // show errors for files that exceed the max batch size (max simultaneous upload limit)
    filesOverBulkLimit.map((file) =>
      dispatchHook(
        addNegativeToast(
          t('errors.canNotAddBulk', {
            fileName: file.name,
            maxBatchSize,
          }),
        ),
      ),
    );

    // with the remaining "good" images, attempt to read them as an image and separate them as valid
    // or invalid
    const promises = goodFiles.map((newFile) => processImage(newFile, maxImgDimension, folderId));
    const settledPromises: NewImage[] = [];
    if (promises.length) {
      newAssetBatch.current += 1;
    }
    Promise.allSettled(promises)
      .then((results) => {
        settledPromises.push(
          ...results.map((result, idx) => {
            if (result.status === PromiseStatus.Fulfilled) {
              return {
                ...result.value,
                id: `asset_${newAssetBatch.current}-${idx}`,
                processStatus: PromiseStatus.Fulfilled,
              };
            } else {
              return {
                // could do something with result.reason.error and/or result.reason.msg
                ...result.reason.imgData,
                id: `asset_${newAssetBatch.current}-${idx}`,
                processStatus: PromiseStatus.Rejected,
              };
            }
          }),
        );
        // add file type errors
        settledPromises.push(
          ...filesIncorrectType.map((file, idx) => {
            return {
              ...getInvalidImgObj(
                file,
                t('errors.fileValidation.type', {
                  fileType: file.type || t('errors.unknownType'),
                }),
              ),
              id: `asset_${newAssetBatch.current}-fte-${idx}`,
            };
          }),
        );
        // add file size errors
        settledPromises.push(
          ...filesIncorrectSize.map((file, idx) => {
            return {
              ...getInvalidImgObj(
                file,
                t('errors.fileValidation.size', {
                  fileSize: localizeFileSize(file.size, ByteUnit.MB).formatted,
                  fileSizeLimit: localizeFileSize(maxFileSize, ByteUnit.MB).formatted,
                }),
              ),
              id: `asset_${newAssetBatch.current}-fse-${idx}`,
            };
          }),
        );
        // add concurrency limit errors
        settledPromises.push(
          ...filesOverBulkLimit.map((file, idx) => {
            return {
              ...getInvalidImgObj(file, t('errors.fileValidation.bulk', { maxBatchSize })),
              id: `asset_${newAssetBatch.current}-cle-${idx}`,
            };
          }),
        );
        //  to the existing list
        setPendingImages((pi) => [...pi, ...settledPromises]);
      })
      .finally(() => {
        // basic information has been collected about the new potential uploads
        if (cb) {
          cb();
        }
        if (!canEditBeforeUpload) {
          // only attempt upload for this batch of new files
          handleUpload(settledPromises);
        }
      });
  };

  const handleEditFiles = (ids: number[]) => {
    const editableAssets = assets
      .filter((asset) => ids.includes(asset.id))
      .map((asset, idx) => ({
        id: `asset_1-${idx}`,
        file: {
          altText: asset.altText,
          name: asset.assetName,
        },
        selected: {},
        isSelected: false,
        processStatus: PromiseStatus.Fulfilled,
        uploadStatus: PromiseStatus.Fulfilled,
        asset,
      }));
    setPendingImages(editableAssets as NewImage[]);
    onEditFilesIndex();
  };

  const handleSaveEdits = async () => {
    const assetsWithChanges = getEditChanges(pendingImages);

    try {
      const updatePromises = assetsWithChanges.map((asset) => {
        const reqData = {
          assetName: asset.file.name,
          altText: asset.file.altText,
        };
        return updateAssetData(asset.asset!.id, reqData);
      });

      const results = await Promise.all(updatePromises);

      results.forEach((res) => {
        dispatchHook(
          addPositiveToast(
            <p>
              {t('success.assetUpdated')}
              <strong>{res.data.assetName}</strong>
            </p>,
          ),
        );
      });

      refetchAssets();
      setPendingImages([]);
      onCloseTakeover(false, noop);

      // If there's an onUploadComplete handler for the index view, call it
      if (onUploadCompleteIndex) {
        onUploadCompleteIndex({
          filesToDismiss: [],
          newFiles: [],
          removeSelectedUploads,
          setPendingImages,
          uploadFailures: [],
          uploadSuccesses: [],
        });
      }
    } catch (error) {
      dispatchHook(addNegativeToast(t('errors.saveEdits')));

      // Report error to DataDog
      const errorName = 'ASSET_EDIT_FAILURE';
      const errorMessage = 'Creative Library Edit Failure';
      const componentError = new Error(errorMessage);
      componentError.name = errorName;

      RUMHelpers.addRUMError(
        componentError,
        {
          name: errorName,
          productName: Product.CREATIVE_LIBRARY,
          teamName: Team.CONTENT_TEAM,
        },
        {
          editFailureDetail: error,
        },
      );
    }
  };

  // should only be called if canEditBeforeUpload
  const setPendingImageMod = (id: string, mods: ImageMods) => {
    const newPendingImages = [...pendingImages];
    const selectedImageId = pendingImages.findIndex((pImg) => pImg.id === id);
    // only execute if image is found
    if (selectedImageId >= 0) {
      newPendingImages[selectedImageId].upload = {
        ...newPendingImages[selectedImageId].upload,
        ...mods,
      };
      setPendingImages(newPendingImages);
    }
  };

  const updatePendingImagesDestination = (
    ids: string[],
    destinationFolderId: number,
    destinationFolderName: string,
  ) => {
    setPendingImages((pendingImgs) =>
      pendingImgs.map((img) =>
        ids.includes(img.id)
          ? {
              ...img,
              selected: {
                ...img.selected,
                destinationFolderId,
                destinationFolderName:
                  destinationFolderName === '__root__' ? ROOT_FOLDER_NAME : destinationFolderName,
              },
            }
          : img,
      ),
    );
  };

  const context: UploadAssetsContextState = {
    activeDropZone,
    canEditBeforeUpload,
    clearPendingUploads,
    editingImage,
    handleNewFiles,
    handleSaveEdits,
    handleUploadPending,
    maxBatchSize,
    maxFileSize,
    maxImgDimension,
    onEditFiles: handleEditFiles,
    onModifyPendingAssets: handleModifyPendingAssets,
    pendingImages,
    pendingUploadCount, // only the pending images that can still be uploaded
    removeCompletedUploads,
    removeSelectedUploads,
    setActiveDropZone,
    setEditingImage,
    setMaxBatchSize,
    setMaxFileSize,
    setMaxImgDimension,
    setPendingImageMod,
    setSelectedAssets,
    updatePendingImagesDestination,
    isUploading,
  };

  return <UploadAssetsContext.Provider value={context}>{children}</UploadAssetsContext.Provider>;
};
